import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import Button from '../../../components/common/Button';
import Card from '../../../components/common/Card';
import FormField from '../../../components/common/FormField';
import AdminLayout from '../../../components/layout/AdminLayout';
import CourseRequestTable from '../../../components/tables/CourseRequestTable';
import { COURSE_REQUEST_STATUS_VALUES } from '../../../constants/CourseRequestStatusConstants';
import { useCourseRequestSearch } from '../../../hooks/useCourseRequestApi';
import {
  CourseRequestDTO,
  CourseRequestSearchRequestDTO,
  CourseRequestSearchRequestDTOStatus,
} from '../../../services/apiClient';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  ...Object.entries(COURSE_REQUEST_STATUS_VALUES).map(([label, value]) => ({
    value: String(value),
    label,
  })),
];

// Opens on Pending: the screen is a queue first and a history second.
const DEFAULT_FILTERS: Partial<CourseRequestSearchRequestDTO> = {
  status:
    COURSE_REQUEST_STATUS_VALUES.Pending as CourseRequestSearchRequestDTOStatus,
};

const CourseRequestList: React.FC = () => {
  const [formFilters, setFormFilters] =
    useState<Partial<CourseRequestSearchRequestDTO>>(DEFAULT_FILTERS);
  const [searchFilters, setSearchFilters] =
    useState<Partial<CourseRequestSearchRequestDTO>>(DEFAULT_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<CourseRequestDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { search } = useCourseRequestSearch();
  const navigate = useNavigate();

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Course Requests', href: '#' },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize);

  const fetchItems = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await search({
        page: currentPage,
        pageSize,
        filters: searchFilters,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching Course Requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // fetchItems is rebuilt every render; these three are what it actually reads. Same shape as
    // every other admin list page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchFilters]);

  const handleSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchFilters(formFilters);
  };

  const handleClearSearch = (): void => {
    setFormFilters(DEFAULT_FILTERS);
    setSearchFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/course-requests"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.Course.View"
    >
      <Card title="Course Requests" subtitle={`Total Records: ${totalRecords}`}>
        <form onSubmit={handleSearch} className="mb-4">
          <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              id="courseName"
              label="Search by Course"
              value={formFilters.courseName || ''}
              onChange={(e) =>
                setFormFilters({ ...formFilters, courseName: e.target.value })
              }
              placeholder="Search by Course"
            />
            <FormField
              id="status"
              label="Status"
              type="dropdown"
              value={
                formFilters.status != null ? String(formFilters.status) : ''
              }
              onChange={(e) =>
                setFormFilters({
                  ...formFilters,
                  status:
                    e.target.value === ''
                      ? undefined
                      : (Number(
                          e.target.value,
                        ) as CourseRequestSearchRequestDTOStatus),
                })
              }
              options={STATUS_OPTIONS}
            />
          </div>

          <div className="mb-4 flex space-x-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <CourseRequestTable
          data={items}
          handleView={(dto) => navigate(`/admin/course-requests/${dto.id}`)}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default CourseRequestList;
